import searchIcon from "@public/img/search.svg";
import { Pagination } from "@/lib/api";
import { AgentFullData } from "@/entry/server/agents";
import PaginationView from "@/component/commom/pagination";
import AgentItem from "./agent-item";
import React, { useEffect, useRef, useState } from "react";
import { agentRepo } from "@/repo/agent-repo";
import SectionTopBg from "@public/img/section-top-bg.png";
import "@/styles/agents.css";

type AgentsGridProps = {
    type: "directors" | "actors"
    defaultAgents: AgentFullData[];
    defaultPagination: Pagination;
}

export default function AgentsGrid({ type, defaultAgents, defaultPagination }: AgentsGridProps) {
    const [agents, setAgents] = useState<AgentFullData[]>(defaultAgents);
    const [pagination, setPagination] = useState<Pagination>(defaultPagination);
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState("")

    const onPageChange = async (page: number) => {
        setIsLoading(true);
        const response = await agentRepo.getAgents(type, searchText, page);
        if (response.status) {
            setAgents(response.data ?? []);
            setPagination(response.pagination!);

            const url = new URL(window.location.href);
            url.searchParams.set("search", searchText);
            url.searchParams.set("page", page.toString());
            window.history.pushState({}, "", url.toString());
        }
        setIsLoading(false);
    }

    const onSearchClick = async (text: string) => {
        if (text.trim() === "") {
            setAgents(defaultAgents);
            setPagination(defaultPagination);
            return;
        }
        setIsLoading(true);
        setSearchText(text);
        const response = await agentRepo.getAgents(type, text, 1);
        if (response.status) {
            setAgents(response.data ?? []);
            setPagination(response.pagination!);

            const url = new URL(window.location.href);
            url.searchParams.set("search", text);
            url.searchParams.set("page", "1");
            window.history.pushState({}, "", url.toString());
        }
        setIsLoading(false);
    }

    if (isLoading) {
        return (
            <div className="page-loading">
                <div className="loader" />
            </div>
        );
    }

    return (
        <div role="main" className="agents-page">
            <img src={SectionTopBg} alt="" className="agents-page__bg" />
            <div className="agents-page__inner">
                <h1 className="agents-page__title">{type === "directors" ? "کارگردان ها" : "بازیگر ها"}</h1>
                <SearchForm type={type} defaultValue={searchText} onSearchClick={onSearchClick} />
                <div className="agents-page__grid">
                    {agents.map((agent) => (
                        <div key={agent.id} className="agents-page__grid-item">
                            <AgentItem
                                type={type}
                                image={agent.image_url}
                                name={agent.name}
                                slug={agent.slug}
                                birth_date={agent.birth_date}
                                birth_place={agent.birth_place}
                                imdb_id={agent.imdb_id}
                            />
                        </div>
                    ))}
                </div>
                <div className="agents-page__pagination">
                    <PaginationView pagination={pagination} onChange={onPageChange} />
                </div>
            </div>
        </div>
    );
}

function SearchForm({ type, defaultValue, onSearchClick }: { type: "directors" | "actors", defaultValue: string, onSearchClick: (text: string) => void }) {
    const searchRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (defaultValue && searchRef.current) {
            searchRef.current.value = defaultValue;
        }

        const input = searchRef.current;
        if (!input) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                onSearchClick(input.value);
            }
        };

        input.addEventListener("keydown", onKeyDown);
        return () => input.removeEventListener("keydown", onKeyDown);
    }, [defaultValue, onSearchClick]);

    return (
        <div className="agents-page__search">
            <button
                type="button"
                className="agents-page__search-btn"
                onClick={() => onSearchClick(searchRef.current ? searchRef.current.value : "")}
            >
                <img src={searchIcon} alt="جستجو" />
            </button>
            <div className="agents-page__search-field">
                <input
                    ref={searchRef}
                    name="search"
                    type="text"
                    className="agents-page__search-input"
                    placeholder={`جستجو نام ${type === "directors" ? "کارگردان" : "بازیگر"}`}
                />
            </div>
        </div>
    );
}
